import React, { useState } from 'react';
import axios from 'axios';
import { Loader2, ShieldCheck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/** Request + enter email OTP (email-only — never show code on screen). */
export default function BookingOtpPanel({
  slug, primary, name, email, phone, onVerified, onBack,
}) {
  const [challengeId, setChallengeId] = useState('');
  const [otp, setOtp] = useState('');
  const [masked, setMasked] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const sendOtp = async () => {
    setError('');
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/tenant/${slug}/book/request-otp`, {
        name, email, phone: phone || '',
      });
      setChallengeId(data.challenge_id);
      setMasked(data.masked_email || email);
      setSent(true);
      setInfo(data.message || 'Verification code sent to your email.');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not send OTP email.');
    }
    setBusy(false);
  };

  const confirm = () => {
    if (!challengeId || otp.trim().length < 4) {
      setError('Enter the OTP from your email');
      return;
    }
    onVerified({ challenge_id: challengeId, otp: otp.trim() });
  };

  return (
    <div className="border-t border-white/10 pt-4 mt-4 space-y-2.5" data-testid="booking-otp-panel">
      <div className="flex items-center justify-between mb-1">
        <p className="text-[11px] uppercase tracking-wider text-zinc-400 flex items-center gap-1">
          <ShieldCheck className="h-3.5 w-3.5" /> Confirm email
        </p>
        <button type="button" onClick={onBack} className="text-[11px] text-zinc-500 hover:text-zinc-300">Back</button>
      </div>
      <p className="text-[12px] text-zinc-400">
        We email a one-time code to <span className="text-zinc-200">{email}</span>. It will not appear on this screen.
      </p>
      {!sent ? (
        <button
          type="button"
          onClick={sendOtp}
          disabled={busy}
          className="w-full py-3 rounded-full font-semibold text-white disabled:opacity-60"
          style={{ background: primary }}
          data-testid="booking-send-otp"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin inline" /> : null} Send verification email
        </button>
      ) : (
        <>
          <p className="text-[11px] text-emerald-300/90">{info} {masked ? `(${masked})` : ''}</p>
          <input
            placeholder="Enter OTP from email"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            inputMode="numeric"
            maxLength={8}
            className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm text-white tracking-widest"
            data-testid="booking-otp-input"
          />
          <button
            type="button"
            onClick={confirm}
            className="w-full py-3 rounded-full font-semibold text-white"
            style={{ background: primary }}
            data-testid="booking-confirm-otp"
          >
            Verify & confirm appointment
          </button>
          <button type="button" onClick={sendOtp} className="w-full text-[11px] text-zinc-500 hover:text-zinc-300">
            Resend code
          </button>
        </>
      )}
      {error && <p className="text-[11px] text-red-400 bg-red-500/10 border border-red-400/30 rounded p-2">{error}</p>}
    </div>
  );
}
